/**
 * デバッグ用状態収集ロジック
 *
 * アプリケーションの各種状態を収集してデバッグIssueに含める
 */

import type { AIFeatureType, AutoCollectedData, ConsoleErrorEntry, DebugChatMessage } from './types';

// グローバルなコンソールエラーバッファ
let consoleErrorBuffer: ConsoleErrorEntry[] = [];
const MAX_ERROR_BUFFER_SIZE = 20;

// 初期化済みフラグ（HMR時の多重パッチを防止）
let isConsoleCaptureInitialized = false;

// クリーンアップ用の参照
let errorListener: ((event: ErrorEvent) => void) | null = null;
let rejectionListener: ((event: PromiseRejectionEvent) => void) | null = null;

const TURBOPACK_HMR_NOISE_PREFIX = '[Turbopack HMR]';

/** Turbopack HMR の dev 専用内部診断（async loader パターン不一致等）。アプリ計装で overlay に再帰属させない。 */
export function isFrameworkInternalConsoleNoise(args: unknown[]): boolean {
  const first = args[0];
  return typeof first === 'string' && first.startsWith(TURBOPACK_HMR_NOISE_PREFIX);
}

/**
 * コンソールエラーのキャプチャを開始
 * アプリ起動時に一度だけ呼び出す
 *
 * @returns クリーンアップ関数（イベントリスナーを削除）
 */
export function initConsoleCapture(): (() => void) | void {
  if (typeof window === 'undefined') return;

  // 既に初期化済みの場合はスキップ（HMR対策）
  if (isConsoleCaptureInitialized) return;
  isConsoleCaptureInitialized = true;

  /* eslint-disable no-console */
  const originalError = console.error;
  const originalWarn = console.warn;
  const safeError = typeof originalError === 'function' ? originalError : null;
  const safeWarn = typeof originalWarn === 'function' ? originalWarn : null;

  console.error = (...args: unknown[]) => {
    if (isFrameworkInternalConsoleNoise(args)) return;
    captureConsoleEntry('error', args);
    if (safeError) {
      scheduleConsoleCall(() => {
        safeError.apply(console, args);
      });
    }
  };

  console.warn = (...args: unknown[]) => {
    if (isFrameworkInternalConsoleNoise(args)) return;
    captureConsoleEntry('warn', args);
    if (safeWarn) {
      scheduleConsoleCall(() => {
        safeWarn.apply(console, args);
      });
    }
  };
  /* eslint-enable no-console */

  // 未処理のエラーもキャプチャ
  errorListener = (event: ErrorEvent) => {
    captureConsoleEntry('error', [event.message], event.error?.stack);
  };
  rejectionListener = (event: PromiseRejectionEvent) => {
    captureConsoleEntry('error', ['Unhandled Promise Rejection:', event.reason]);
  };

  window.addEventListener('error', errorListener);
  window.addEventListener('unhandledrejection', rejectionListener);

  // クリーンアップ関数を返す
  return () => {
    if (errorListener) {
      window.removeEventListener('error', errorListener);
      errorListener = null;
    }
    if (rejectionListener) {
      window.removeEventListener('unhandledrejection', rejectionListener);
      rejectionListener = null;
    }
    isConsoleCaptureInitialized = false;
  };
}

function captureConsoleEntry(level: 'error' | 'warn', args: unknown[], stack?: string): void {
  const message = args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      try {
        return JSON.stringify(arg, null, 2);
      } catch {
        return String(arg);
      }
    })
    .join(' ');

  const entry: ConsoleErrorEntry = {
    level,
    message: message.slice(0, 1000), // 長すぎるメッセージは切り詰め
    timestamp: new Date().toISOString(),
    stack: stack?.slice(0, 2000),
  };

  consoleErrorBuffer.push(entry);

  // バッファサイズ制限
  if (consoleErrorBuffer.length > MAX_ERROR_BUFFER_SIZE) {
    consoleErrorBuffer = consoleErrorBuffer.slice(-MAX_ERROR_BUFFER_SIZE);
  }
}

function scheduleConsoleCall(callback: () => void): void {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(callback);
    return;
  }

  Promise.resolve()
    .then(callback)
    .catch(() => {
      setTimeout(callback, 0);
    });
}

/**
 * 現在の環境を判定
 */
function detectEnvironment(): AutoCollectedData['environment'] {
  if (typeof window === 'undefined') return 'unknown';

  const url = window.location.href;

  if (url.includes('kakusill.atsumell.com')) return 'production';
  if (url.includes('staging.')) return 'staging';
  if (url.includes('sandbox') || url.includes('vercel.app')) return 'sandbox';
  if (url.includes('localhost')) return 'development';

  return 'unknown';
}

/**
 * 現在のAI機能を検出
 */
function detectAIFeature(): AIFeatureType {
  if (typeof window === 'undefined') return 'other';

  const path = window.location.pathname;

  if (path.includes('/canvas/')) return 'canvas_chat';
  if (path.includes('/mermaid/')) return 'mermaid';

  // デフォルトはページチャット
  return 'page_chat';
}

/**
 * ブラウザ情報を取得
 */
function getBrowserInfo(): string {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;

  // シンプルなブラウザ検出
  if (ua.includes('Chrome')) {
    const match = ua.match(/Chrome\/(\d+)/);
    return `Chrome ${match?.[1] || ''}`;
  }
  if (ua.includes('Firefox')) {
    const match = ua.match(/Firefox\/(\d+)/);
    return `Firefox ${match?.[1] || ''}`;
  }
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/(\d+)/);
    return `Safari ${match?.[1] || ''}`;
  }

  return ua.slice(0, 100);
}

/**
 * Valtioストアから状態を収集
 */
async function collectStoreState(): Promise<AutoCollectedData['appState']> {
  try {
    // 動的インポートでストアを取得
    const { chatStore, pageStore, uiStore } = await import('@/stores/adapters/globalStoreAdapter');
    const { selectionContextStore } = await import('@/stores/selectionContextStore');

    const selectedPageId = uiStore.selectedPageId;
    const selectedPage = selectedPageId ? pageStore.pages[selectedPageId] : null;

    return {
      selectedPageId,
      selectedPageTitle: selectedPage?.title || null,
      isAIGenerating: chatStore.isAIGenerating,
      wsProgress: chatStore.wsProgress,
      wsStatus: chatStore.wsStatus,
      isDirty: pageStore.currentEditingContent?.isDirty || false,
      selectionContext: selectionContextStore.selectionContext?.context || null,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to collect store state:', error);
    return {
      selectedPageId: null,
      selectedPageTitle: null,
      isAIGenerating: false,
      wsProgress: undefined,
      wsStatus: undefined,
      isDirty: false,
      selectionContext: null,
    };
  }
}

/**
 * チャット履歴を収集する。
 *
 * Chat V2 の debugChatStore は書き手が無く削除したため、ここでは空を返す。
 */
async function collectChatHistory(): Promise<{
  history: DebugChatMessage[];
  lastUserInput?: string;
}> {
  return { history: [] };
}

/**
 * 選択ソースを収集
 */
async function collectSelectedSources(): Promise<AutoCollectedData['selectedSources']> {
  try {
    const { chatStore } = await import('@/stores/adapters/globalStoreAdapter');

    const sourceIds = chatStore.rawSelectedSourceIds || [];

    if (sourceIds.length === 0) {
      return [{ type: 'none' }];
    }

    // ソースIDからタイプを推定（簡易版）
    return sourceIds.map((id) => {
      if (id.startsWith('pdf_') || id.includes('.pdf')) {
        return { type: 'pdf' as const, id };
      }
      return { type: 'page' as const, id };
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to collect selected sources:', error);
    return [{ type: 'none' }];
  }
}

/**
 * すべての自動収集データを取得
 */
export async function collectDebugData(): Promise<AutoCollectedData> {
  const [appState, chatData, selectedSources] = await Promise.all([
    collectStoreState(),
    collectChatHistory(),
    collectSelectedSources(),
  ]);

  return {
    // 基本情報
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : '',
    environment: detectEnvironment(),
    browser: getBrowserInfo(),
    screenSize: {
      width: typeof screen !== 'undefined' ? screen.width : 0,
      height: typeof screen !== 'undefined' ? screen.height : 0,
    },
    viewportSize: {
      width: typeof window !== 'undefined' ? window.innerWidth : 0,
      height: typeof window !== 'undefined' ? window.innerHeight : 0,
    },

    // AI機能情報
    aiFeature: detectAIFeature(),
    selectedSources,

    // チャット履歴
    chatHistory: chatData.history,
    lastUserInput: chatData.lastUserInput,

    // アプリ状態
    appState,

    // エラー情報
    consoleErrors: [...consoleErrorBuffer].slice(-10), // 直近10件
  };
}

/**
 * コンソールエラーバッファをクリア
 */
export function clearConsoleErrorBuffer(): void {
  consoleErrorBuffer = [];
}
